import type { OrderlineView } from '../../../../../services/orderlines/orderlines.service';
import type { OrderlineSelection } from '../../receipt/actions/item-discount-select-modal/ItemDiscountSelectModal';
import { roundMoney } from '../../../../../utils/money';
import { allocateMoneyPortion } from 'shared-models/browser';

export const MIN_ITEM_PAYMENT_REMAINDER = 0.01;

const getSelectedLine = (
  orderlines: OrderlineView[],
  selection: OrderlineSelection,
): OrderlineView | undefined => orderlines.find((line) => line.id === selection.orderlineId);

const getLineNetSubtotal = (
  line: OrderlineView,
  quantity: number,
): number => {
  return allocateMoneyPortion(line.unitPrice * line.quantity - (line.discountInput ?? 0),
    line.quantity, line.itemPaidQuantity ?? 0, quantity, line.roundAmounts ?? false);
};

export const getSelectableItemPaymentQuantity = (line: OrderlineView): number => Math.max(
  0,
  line.quantity - (line.itemPaidQuantity ?? 0),
);

// Every unit already paid — locked in a transfer; rides along only on a full move.
export const isFullyPaidLine = (line: OrderlineView): boolean =>
  line.quantity > 0 && (line.itemPaidQuantity ?? 0) >= line.quantity;

// Net of the still-payable portion; fully-comped (net 0) lines are not payable per item.
export const getSelectableItemPaymentNet = (line: OrderlineView): number => {
  const quantity = getSelectableItemPaymentQuantity(line);
  if (quantity <= 0) {
    return 0;
  }
  return getLineNetSubtotal(line, quantity);
};

export const isItemPaymentSelectableLine = (line: OrderlineView): boolean =>
  line.status !== 'pending' && getSelectableItemPaymentNet(line) > 0;

export const hasSelectableItemPaymentLines = (orderlines: OrderlineView[]): boolean =>
  orderlines.some(isItemPaymentSelectableLine);

const canFitCurrentPaymentline = (
  line: OrderlineView,
  maxSubtotal: number,
): boolean => {
  if (!(maxSubtotal > 0)) {
    return false;
  }

  if (getSelectableItemPaymentQuantity(line) <= 0) {
    return false;
  }

  return computeItemPaymentSplitAmount(
    [{ orderlineId: line.id, quantity: 1 }],
    [line],
  ) <= maxSubtotal;
};

export const hasItemPaymentSplitOptions = (
  orderlines: OrderlineView[],
  paymentAmount: number,
): boolean => {
  const maxSubtotal = getItemPaymentSelectionCap(paymentAmount);
  return orderlines.some(
    (line) => line.status !== 'pending' && canFitCurrentPaymentline(line, maxSubtotal),
  );
};

export const computeItemPaymentSplitAmount = (
  selections: OrderlineSelection[],
  orderlines: OrderlineView[],
): number => {
  if (selections.length === 0) {
    return 0;
  }

  const selectedSubtotal = selections.reduce((sum, selection) => {
    const line = getSelectedLine(orderlines, selection);
    if (!line) {
      return sum;
    }
    return sum + getLineNetSubtotal(line, selection.quantity);
  }, 0);

  if (!(selectedSubtotal > 0)) {
    return 0;
  }

  return roundMoney(selectedSubtotal, getSelectedLine(orderlines, selections[0])?.roundAmounts ?? false);
};

export const getItemPaymentSelectionCap = (paymentAmount: number): number => Math.max(
  0,
  paymentAmount - MIN_ITEM_PAYMENT_REMAINDER,
);
